<template>
  <DeepHeader
    v-model:locale="locale"
    v-model:menu-user="userMenu"
    :available-locales="availableLocales"
    :env="currentEnv"
    hide-app-switch
    :menu-user-items="menuItems"
    :user="{
      displayName: user?.profile?.name ?? '',
      email: user?.profile?.email,
    }"
  >
    <template #prepend>
      <v-btn
        variant="plain"
        :to="{ name: '/[[space]]/kb/' }"
        class="pr-0 ml-3"
        :class="{
          'ml-3 pl-1': mdAndDown,
        }"
        size="x-small"
      >
        <template #prepend>
          <div class="d-flex align-center gc-1">
            <img
              src="/svg/abacus-logo.svg"
              height="20"
              aria-hidden="true"
              class="d-block"
            />
            <img
              v-if="!mdAndDown"
              src="/svg/abasupport-2.0.svg"
              aria-hidden="true"
              class="d-block pt-1"
            />
          </div>
        </template>
      </v-btn>
      <v-container
        class="overflow-x-hidden"
        :class="{
          'pl-2': mdAndDown,
        }"
      >
        <TheAppBreadcrumbs />
      </v-container>
    </template>
    <template #append-start>
      <div v-if="!xs" class="d-flex align-center ga-2 mr-2">
        <LiveStatusButton />
        <v-btn-secondary
          v-if="canCreateWishTicket"
          size="small"
          @click="showWishTicketDialog = true"
        >
          {{ t('wish_ticket.open') }}
        </v-btn-secondary>
      </div>
    </template>
  </DeepHeader>
  <NotificationSettingsDialog v-model="showNotificationSettingsDialog" />
  <WishTicketDialog v-if="canCreateWishTicket" v-model="showWishTicketDialog" />
</template>

<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { DeepHeader } from '@deepcloud/deep-ui-lib'
import { useAuth } from '@/composables/use-auth'
import { useUserPermissions } from '@/composables/use-user-permissions'
import LiveStatusButton from './live-status/LiveStatusButton.vue'
import NotificationSettingsDialog from './notifications/NotificationSettingsDialog.vue'
import TheAppBreadcrumbs from './TheAppBreadcrumbs.vue'
import WishTicketDialog from './wish-ticket/WishTicketDialog.vue'
import type { MenuUserItem } from '@deepcloud/deep-ui-lib'

defineProps({
  color: {
    type: String,
    default: 'primary',
  },
  drawerBtnDisabled: {
    type: Boolean,
    default: false,
  },
})

const { t, locale, availableLocales } = useI18n()
const { user, logout } = useAuth()
const { permissions } = useUserPermissions()

const canCreateWishTicket = computed(
  () =>
    !permissions.value.is_ambassador && !permissions.value.is_ambassador_plus,
)

const { mdAndDown, xs } = useDisplay()

const currentEnv = import.meta.env.VITE_ENVIRONMENT
const userMenu = ref(false)

watch(
  () => locale.value,
  async (newValue) => {
    if (newValue) {
      menu.value = false
    }
  },
)

const menu = ref(false)
const showNotificationSettingsDialog = ref(false)
const showWishTicketDialog = ref(false)

const menuItems = computed<MenuUserItem[]>(() => [
  {
    key: 'language',
    title: t('actions.language'),
  },
  {
    key: 'notification-settings',
    title: t('notifications.settings.title'),
    action: () => {
      // first hide the user menu to not stay open in the background
      userMenu.value = false
      showNotificationSettingsDialog.value = true
    },
  },
  {
    key: 'support',
    title: t('actions.support_request'),
    prepend: { icon: 'far fa-envelope' },
    href: 'mailto:abasupport.support@abacus.ch',
  },
  {
    divider: true,
    space: 'ma-2',
  },
  {
    key: 'logout',
    title: t('actions.logout'),
    action: () => logout(),
  },
])
</script>
